/**
 * CustomerInfoCard - 기본 고객 정보 카드
 *
 * 고객의 기본 정보(이름, 전화번호, 생년월일, 등록일, 메모)를 표시
 * 전화번호는 크고 눈에 잘 보이도록 강조 표시
 */

import {
  User,
  Phone,
  Cake,
  Calendar,
  Ban,
  CheckCircle,
  AlertTriangle,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  formatPhoneNumber,
  formatBirthDate,
  getDisplayName,
  phoneNumbersDiffer,
} from "./utils";
import { formatDateWithDay } from "@/utils/dateFormatter";
import { customerApi } from "@/api/customer";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Customer } from "@/types/customer";

export interface CustomerInfoCardProps {
  customer: Customer;
  /**
   * 컴팩트 모드 - 상담 상세 등에서 간단히 표시할 때
   */
  compact?: boolean;
  /**
   * 카드 없이 내용만 표시
   */
  noCard?: boolean;
  /**
   * 통화의 실제 발신번호(caller_id). 전달되면, 분석이 저장한 customer.phone과 비교해
   * 다를 경우 "실제 발신번호와 다름 — 확인 필요" 경고를 표시한다.
   * (STT 숫자 오인식으로 잘못된 번호의 고객이 생성된 케이스 안내용)
   */
  callerNumber?: string;
}

export function CustomerInfoCard({
  customer,
  compact = false,
  noCard = false,
  callerNumber,
}: CustomerInfoCardProps) {
  const queryClient = useQueryClient();
  const displayInfo = getDisplayName(customer);

  // 실제 발신번호(caller_id)와 분석이 저장한 고객 전화번호가 다르면 "확인 필요" 경고를 띄운다.
  // STT 숫자 오인식 등으로 고객 레코드에 실제 발신자와 다른 번호가 들어간 케이스 안내.
  const phoneMismatch = phoneNumbersDiffer(callerNumber, customer.phone);
  const phoneMismatchWarning = phoneMismatch ? (
    <div className="flex items-start gap-2 p-2 rounded-md bg-destructive/10 border border-destructive/40">
      <AlertTriangle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
      <div className="text-xs text-destructive leading-relaxed">
        <p className="font-semibold">실제 발신번호와 다릅니다 — 확인 필요</p>
        <p>
          이 번호는 통화 중 AI가 인식한 번호로, 실제 발신번호(
          <strong>{formatPhoneNumber(callerNumber)}</strong>)와 달라요. 스크립트
          또는 통화 내용을 확인하세요.
        </p>
      </div>
    </div>
  ) : null;

  // AI 이름 확인 mutation
  const confirmNameMutation = useMutation({
    mutationFn: () =>
      customerApi.updateCustomer(customer.id, {
        name: customer.aiDetectedName || "",
        phone: customer.phone || "",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
    },
  });

  const genderLabel =
    customer.gender === "MALE"
      ? "남성"
      : customer.gender === "FEMALE"
        ? "여성"
        : null;

  const content = compact ? (
    // 컴팩트 모드: 이름 + 전화번호만 강조
    <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-lg p-4 space-y-3">
      <div className="flex items-center gap-2">
        <h2 className="text-2xl font-bold">{displayInfo.name}</h2>
        {displayInfo.isAi && (
          <Badge
            variant="outline"
            className="text-[10px] px-1.5 py-0 text-violet-600 border-violet-200 bg-violet-50"
          >
            AI 감지
          </Badge>
        )}
      </div>
      <div className="space-y-2">
        {customer.phone && (
          <div className="flex items-center gap-2">
            <Phone className="h-4 w-4 text-muted-foreground" />
            <span className="text-lg font-semibold text-primary">
              {formatPhoneNumber(customer.phone)}
            </span>
          </div>
        )}
        {phoneMismatchWarning}
      </div>
    </div>
  ) : (
    // 풀 모드: 모든 정보 표시
    <div className="space-y-3">
      <h4 className="font-medium text-sm text-muted-foreground flex items-center gap-2">
        <User className="w-4 h-4" />
        기본 정보
      </h4>

      {/* 전화번호 강조 표시 */}
      <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-lg p-3">
        <div className="flex items-center gap-2">
          <Phone className="h-5 w-5 text-primary" />
          <span className="text-xl font-bold text-primary tracking-wide">
            {formatPhoneNumber(customer.phone)}
          </span>
        </div>
      </div>

      {phoneMismatchWarning}

      {/* AI 이름 감지 알림 */}
      {customer.aiDetectedName &&
        !customer.nameConfirmed &&
        customer.aiDetectedName.trim().length > 0 && (
          <div className="flex items-center gap-2 p-2 rounded-md bg-violet-50 border border-violet-200">
            <span className="text-xs text-violet-700">
              AI 감지 이름: <strong>{customer.aiDetectedName}</strong>
            </span>
            <Button
              variant="outline"
              size="sm"
              className="h-6 text-xs px-2 text-violet-700 border-violet-300 hover:bg-violet-100"
              onClick={() => confirmNameMutation.mutate()}
              disabled={confirmNameMutation.isPending}
            >
              <CheckCircle className="w-3 h-3 mr-1" />
              확인
            </Button>
          </div>
        )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-xs text-muted-foreground">이름</p>
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-medium">{displayInfo.name}</p>
            {displayInfo.isAi && (
              <Badge
                variant="outline"
                className="text-[9px] px-1 py-0 text-violet-600 border-violet-200 bg-violet-50"
              >
                AI
              </Badge>
            )}
          </div>
        </div>
        {genderLabel && (
          <div>
            <p className="text-xs text-muted-foreground">성별</p>
            <p className="text-sm font-medium">{genderLabel}</p>
          </div>
        )}
        <div>
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <Cake className="w-3 h-3" />
            생년월일
          </p>
          <p className="text-sm font-medium">
            {formatBirthDate(customer.birthDate)}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            등록일
          </p>
          <p className="text-sm">{formatDateWithDay(customer.createdAt)}</p>
        </div>
      </div>

      {/* 예약 차단 상태 */}
      {customer.bookingBlocked && (
        <div className="flex items-center gap-2 p-2 rounded-md bg-destructive/10 border border-destructive/30">
          <Ban className="w-4 h-4 text-destructive" />
          <div>
            <p className="text-xs font-medium text-destructive">예약 차단됨</p>
            {customer.blockReason && (
              <p className="text-xs text-destructive">{customer.blockReason}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );

  if (noCard) {
    return content;
  }

  return (
    <Card>
      <CardContent className="p-4">{content}</CardContent>
    </Card>
  );
}
